import { FieldMetaDataRenderer, FieldMetaDataType } from './action-constants';

export interface TicketCustomDefinitionsMeta {
    id: string;
    tenantID: number;
    label: string;
    defaultValues: Array<Record<string, string>> | null;
    security: any | null;
    securityMap: Record<string, any>;
    fieldMetaDataType: FieldMetaDataType;
    fieldMetaDataRenderer: FieldMetaDataRenderer;
    mandatory: boolean;
    multiValue: boolean;
    expression: any | null;
    conditionalMandatory: boolean;
}

export interface TicketCustomDefinitionsGroup {
    id: string;
    tenantID: number;
    label: string;
    fieldMetas: TicketCustomDefinitionsMeta[];
}

export interface TicketCustomDefinitions {
    id: string;
    tenantID: number;
    label: string;
    fieldGroups: TicketCustomDefinitionsGroup[];
}

export interface AddressTemplateField {
    label: string;
    name: string;
    commaSeparated: boolean;
    mandatory: boolean;
}

export interface AddressTemplates {
    country: string;
    houseNumberAfter: boolean;
    formattedAddress: string;
    fields: AddressTemplateField[][];
}

export interface Country {
    id: string;
    name: string;
    code: string;
    alpha3: string;
    iso31662: string;
    phoneCode: string;
    currencyCode: string;
    currencyName: string;
}

export interface Option {
    label: string;
    value: string;
    selected?: boolean;
    ticketDefinitionIDs?: string[];
}

export interface Region {
    bingID: string;
    code: string;
    countryID: string;
    id: string;
    name: string;
    type: string | null;
}

export interface Location {
    id: string;
    uid: number;
    tenantID: number;
    agencyID: number;
    internalName: string;
    enableRequestReviews: boolean;
    inheritModuleIDs: boolean;
    inheritModuleSources: boolean;
    emailFromForInternal: boolean;
}

export interface User {
    displayName: string;
    email: string;
    id: number;
}

export enum FormCreateTicketControl {
    Appellation = 'appellation',
    ReporterName = 'reporterName',
    Comment = 'comment',
    ReporterEmail = 'reporterEmail',
    ReporterPhone = 'reporterPhone',
    Location = 'location',
    Note = 'note',
    Tags = 'tags',
    Stage = 'stage',
    DueInUnit = 'dueInUnit',
    DueInDuration = 'dueInDuration',
    Region = 'region',
    Country = 'country',
    Assign = 'assign',
    SelectedQueue = 'selectedQueue',
    AssignedTo = 'assignedTo',
    streetAddress2 = 'streetAddress2',
    Locality = 'locality',
    PostalCode = 'postalCode',
    StreetAddress = 'streetAddress',
    StreetAddress2 = 'streetAddress2',
    Attachments = 'attachments',
    CustomFields = 'customFields',
    TicketDefinitionID = 'ticketDefinitionID',
    IsYetiEnabled = 'isYetiEnabled',
    ObjectID = 'objectID',
    UserDefined = 'userDefined',
}

export interface FormCreateTicketInputs {
    [FormCreateTicketControl.Appellation]: number;
    [FormCreateTicketControl.ReporterName]: string;
    [FormCreateTicketControl.Comment]: string;
    [FormCreateTicketControl.ReporterEmail]: string;
    [FormCreateTicketControl.ReporterPhone]: string;
    [FormCreateTicketControl.Location]: Option & Location;
    [FormCreateTicketControl.Tags]: string[];
    [FormCreateTicketControl.Stage]: string;
    [FormCreateTicketControl.DueInUnit]: keyof SLADetail;
    [FormCreateTicketControl.DueInDuration]: number;
    [FormCreateTicketControl.Region]: string;
    [FormCreateTicketControl.Country]: string;
    [FormCreateTicketControl.Assign]: string;
    [FormCreateTicketControl.SelectedQueue]: string;
    [FormCreateTicketControl.AssignedTo]: number;
    [FormCreateTicketControl.Locality]: string;
    [FormCreateTicketControl.PostalCode]: string;
    [FormCreateTicketControl.StreetAddress]: string;
    [FormCreateTicketControl.StreetAddress2]: string;
    [FormCreateTicketControl.Attachments]: string[];
    [FormCreateTicketControl.CustomFields]: Record<string, object>;
    [FormCreateTicketControl.TicketDefinitionID]: string;
    [FormCreateTicketControl.IsYetiEnabled]: boolean;
    [FormCreateTicketControl.ObjectID]: string;
    [FormCreateTicketControl.Note]: string;
    [FormCreateTicketControl.UserDefined]?: UserDefined;
}

export interface FormEditTicketInputs {
    [FormCreateTicketControl.Appellation]: number;
    [FormCreateTicketControl.ReporterName]: string;
    [FormCreateTicketControl.Comment]: string;
    [FormCreateTicketControl.ReporterEmail]: string;
    [FormCreateTicketControl.ReporterPhone]: string;
    [FormCreateTicketControl.Location]: Option & Location;
    [FormCreateTicketControl.Region]: string;
    [FormCreateTicketControl.Country]: string;
    [FormCreateTicketControl.Locality]: string;
    [FormCreateTicketControl.PostalCode]: string;
    [FormCreateTicketControl.StreetAddress]: string;
    [FormCreateTicketControl.StreetAddress2]: string;
}

export interface Normalize {
    addressType: string | null;
    streetAddress: string;
    streetAddress2: string;
    houseNumber: string | null;
    route: string | null;
    locality: string | null;
    region: string | null;
    postalCode: string | null;
    country: string;
    latitude: number;
    longitude: number;
    timeZone: string | null;
    pin: string | null;
}

export type SLADetail = {
    months: number;
    weeks: number;
    days: number;
    hours: number;
    minutes?: number;
};

export enum Level {
    Tenant = 0,
    Agency = 1,
}

interface Address {
    addressType?: string | null;
    streetAddress1: string;
    streetAddress2: string;
    locality: string;
    region: string | null;
    postalCode: string;
    country: string;
    latitude: number;
    longitude: number;
}

interface UserDefined {
    sentiment?: string;
    locationName: string;
}

export interface Ticket {
    id?: number;
    createdDate?: number;
    updatedDate?: number;
    createdBy?: number;
    updatedBy?: number;
    tenantID: number;
    agencyID: number;
    ticketType: number;
    queueID?: string | null;
    locationUid: number;
    locationID: string;
    dueDate: number;
    owner?: string | null;
    objectID?: string;
    comment: string;
    stageID: string;
    tags?: string[] | null;
    keywords?: string[] | null;
    watchers?: string[] | null;
    userDefined: UserDefined;
    priority?: number | null;
    reporterName: string;
    reporterPhone?: string;
    reporterEmail?: string;
    responded?: boolean;
    pendingApproval?: boolean;
    sla: number;
    originalDueDate?: number | null;
    currentStartDate?: number | null;
    closedDate?: number | null;
    closedBy?: number;
    responders?: string[] | null;
    categories?: string[] | null;
    surveyQA?: any[] | null;
    fullComment: string;
    closedExplanation?: string | null;
    escalationID?: string | null;
    ticketDefinitionID?: string | null;
    attachments?: any[];
    version?: number;
    address: Address;
    appellation?: number | null;
    closingReasonID?: string | null;
    surveyTemplateID?: string | null;
    rootCauseIDs?: string[] | null;
    rootCauses?: any[];
    level: number;
    removed?: boolean;
    legacyID?: string | null;
    emailIngestID?: string | null;
    onHoldSum?: number;
    surveyResultID?: string | null;
    nps?: number;
    actionedFrom?: string | null;
    assignedTo?: number;
    assignedBy?: number | null;
    commentID?: string | null;
    userType?: string | null;
    postReviewResponseActionFilter?: string | null;
    originalAssignedDate?: number;
    assignedDate?: number;
    reviewDate?: number | null;
    edited?: boolean;
    closeTimeInMillis?: number;
    responseTimeInMillis?: number;
    executedLambdaName?: string | null;
    slaDetail?: SLADetail;
    editBackDateOnClose?: boolean;
    mrrAgreementID?: string | null;
    parentTicketID?: string | null;
    isCloneTicket?: boolean;
    closedOnTime?: boolean;
    toTenantQueueID?: string | null;
    customFields?: Record<string, object>;
    isYetiEnabled?: boolean;
}

export interface ExplainAndClose {
    reason: string;
    rootCause: { label: string; value: string }[];
    closingReason: { label: string; value: string } | undefined;
    closedDate?: any;
}

export interface LogEntry {
    id: string;
    createdDate: number; // Unix timestamp in milliseconds
    tenantID: number;
    ticketID: number;
    userID: number;
    action: number;
    before: string | null;
    after: string | null;
    closedExplanation: string | null;
    userRoles: string[] | null; // Assuming roles would be an array of strings
    dateField: boolean;
    executedByLambda: string | null;
    displayName: string;
    rootCauses?: string[] | null;
}

export interface LogResponse {
    log: LogEntry[];
}
